import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Mail, QrCode, Smartphone, Share2, Globe, Sparkles,
} from 'lucide-react';
import Footer from '../components/Footer';
import SEO from '../shared/seo/SEO';

const INVITATIONS_FAQS = [
  {
    q: 'Какво представляват дигиталните покани за сватба?',
    a: 'Дигиталните покани за сватба са модерен и удобен начин да поканите гостите си чрез интернет. Вместо хартиени покани, вие създавате сватбен уебсайт, който служи като онлайн покана — гостите получават линк или сканират QR код и веднага виждат цялата информация за сватбата, включително дата, локация, програма и възможност да потвърдят присъствие онлайн.',
  },
  {
    q: 'Как да изпратя онлайн покани за сватба на гостите?',
    a: 'След като създадете вашия сватбен уебсайт, получавате уникален линк и QR код. Можете да ги споделите чрез имейл, Messenger, Viber, SMS или да отпечатате QR кода върху хартиените покани. Всеки гост получава мигновен достъп до цялата информация за сватбата и може да RSVP-не директно от телефона си.',
  },
  {
    q: 'Какви са предимствата на дигиталните покани спрямо хартиените?',
    a: 'Дигиталните покани за сватба са безплатни, екологични и мигновени. Получавате незабавни RSVP потвърждения по имейл без да чакате отговори. Можете да обновявате информацията по всяко време, да добавяте галерия снимки, таймлайн и броене до деня. Гостите винаги имат актуалната информация в джоба си.',
  },
  {
    q: 'Мога ли да комбинирам дигитални и хартиени покани за сватба?',
    a: 'Разбира се! Много двойки използват и двата подхода. Можете да изпратите хартиени покани с QR код, който води към вашия сватбен уебсайт. Така запазвате традицията на хартиената покана, но добавяте удобството на онлайн информацията и RSVP.',
  },
  {
    q: 'Колко струват дигиталните покани за сватба?',
    a: 'Дигиталните покани за сватба са напълно безплатни. Регистрацията и създаването на сватбен уебсайт не струват нищо. Можете да създадете дигитални покани и сватбен сайт с всички функции — галерия, таймлайн, броене и RSVP — безплатно и без никакви ангажименти.',
  },
  {
    q: 'Гостите ми ще могат ли да използват дигиталните покани на телефона си?',
    a: 'Да! Вашият сватбен уебсайт и дигиталните покани за сватба са напълно оптимизирани за мобилни телефони и таблети. Гостите могат да видят поканата, да RSVP-нат, да разгледат галерията със снимки и да следят броенето до сватбата директно от своя смартфон.',
  },
];

function OnlineInvitations() {
  const { currentUser } = useAuth();

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: INVITATIONS_FAQS.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a,
      },
    })),
  };

  return (
    <main className="home">
      <SEO
        title="Дигитални Покани за Сватба — Онлайн Покани за Сватба"
        description="Създайте дигитални покани за сватба и онлайн покани за сватба с Нашата Сватба. Безплатни дигитални покани с QR код, RSVP онлайн, галерия снимки и броене до деня. Идеално за български двойки."
        ogTitle="Дигитални Покани за Сватба и Онлайн Покани — Нашата Сватба"
        ogDescription="Създайте безплатни дигитални покани за сватба и онлайн покани с QR код. Споделете с гостите си чрез имейл, Viber или Messenger. RSVP онлайн, галерия и броене."
        canonical="https://www.nashatasvatba.in/online-pokani"
      />

      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <section className="home-hero" aria-label="Дигитални покани за сватба">
        <div className="home-hero-inner">
          <div className="home-hero-content">
            <span className="home-hero-eyebrow">Онлайн Покани за Сватба</span>
            <h1 className="home-hero-title">
              Дигитални Покани за <em>Сватба</em>
            </h1>
            <p className="home-hero-body">
              Създайте впечатляващи дигитални покани за сватба с вашия персонален сватбен уебсайт. Изпратете онлайн покани за сватба чрез имейл, Messenger, Viber или с QR код. Вашите гости ще видят цялата информация, ще RSVP-нат онлайн и ще следят броенето до големия ден — всичко от телефона си.
            </p>

            <div className="home-hero-actions">
              {currentUser ? (
                <Link to="/dashboard" className="btn btn-primary btn-lg">
                  Към Таблото
                </Link>
              ) : (
                <>
                  <Link to="/register" className="btn btn-primary btn-lg">
                    Създайте Дигитална Покана
                  </Link>
                  <Link to="/login" className="btn btn-secondary btn-lg">
                    Безплатна Регистрация
                  </Link>
                </>
              )}
            </div>
          </div>

          <div className="home-hero-image">
            <img
              src="https://images.unsplash.com/photo-1529636798458-92182e662485?q=80&w=2669&auto=format&fit=crop"
              alt="Дигитални покани за сватба — създайте онлайн покана за сватба с мобилен телефон и QR код"
              loading="eager"
            />
          </div>
        </div>
      </section>

      <section className="home-section home-section-alt" aria-label="Предимства на дигиталните покани за сватба">
        <header>
          <h2 className="home-section-title">
            Защо да Изберете Дигитални Покани за Сватба?
          </h2>
          <p className="home-section-subtitle">
            Онлайн поканите за сватба са модерният, безплатен и удобен начин да поканите гостите си. Ето какво получавате с вашия сватбен уебсайт от Нашата Сватба.
          </p>
        </header>

        <div className="home-features" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
          {[
            {
              Icon: Globe,
              title: 'Мигновен Достъп за Всички',
              desc: 'Вашите дигитални покани за сватба достигат до гостите веднага. Няма забавяне от пощата, няма изгубени покани — само мигновена връзка към вашия сватбен уебсайт.',
            },
            {
              Icon: QrCode,
              title: 'QR Код за Хартиени Покани',
              desc: 'Всяка дигитална покана за сватба идва с уникален QR код. Отпечатайте го върху хартиените покани и гостите сканират с телефона си, за да видят онлайн поканата за сватба.',
            },
            {
              Icon: Mail,
              title: 'RSVP Онлайн Потвърждения',
              desc: 'С онлайн поканите за сватба гостите потвърждават присъствие директно от телефона. Получавате имейл известия за всяко потвърждение — без обаждания и хартиени списъци.',
            },
            {
              Icon: Share2,
              title: 'Споделяне навсякъде',
              desc: 'Изпратете дигиталните покани за сватба чрез имейл, Facebook Messenger, Viber, WhatsApp или SMS. Споделете линка навсякъде, където са вашите гости.',
            },
            {
              Icon: Smartphone,
              title: 'Оптимизирани за Мобилни',
              desc: 'Всички дигитални покани за сватба са перфектни на телефони и таблети. Гостите виждат красивата онлайн покана на всяко устройство.',
            },
            {
              Icon: Sparkles,
              title: 'Напълно Безплатно',
              desc: 'Създайте неограничен брой дигитални покани за сватба безплатно. Няма скрити такси, няма абонаменти — започнете веднага.',
            },
          ].map(({ Icon, title, desc }, i) => (
            <article key={i} className="card card-padding card-hover">
              <div className="home-feature-icon">
                <Icon size={28} />
              </div>
              <h3 className="home-feature-title">{title}</h3>
              <p className="home-feature-desc">{desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="home-section" aria-label="Как работят дигиталните покани">
        <div className="home-cta" style={{ background: 'var(--ns-teal-50)', textAlign: 'left', padding: 'var(--space-12)' }}>
          <h2 className="home-cta-title" style={{ textAlign: 'center' }}>
            Как Работят Дигиталните Покани за Сватба?
          </h2>
          <ol style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--space-6)',
            maxWidth: '700px',
            margin: '0 auto',
            fontSize: 'var(--text-lg)',
            lineHeight: '1.8',
            color: 'var(--ns-gray-700)',
            paddingLeft: 'var(--space-6)',
          }}>
            <li><strong>Регистрирайте се безплатно</strong> — създайте профил за 30 секунди.</li>
            <li><strong>Изберете шаблон</strong> за вашия сватбен уебсайт и дигитални покани.</li>
            <li><strong>Добавете информация</strong> за сватбата — дата, локация, програма и снимки.</li>
            <li><strong>Споделете линка или QR кода</strong> с гостите си чрез имейл, Viber, Messenger.</li>
            <li><strong>Гостите виждат онлайн поканата</strong> и RSVP-ват директно от телефона си.</li>
            <li><strong>Получавате имейл известия</strong> за всяко потвърждение на присъствие.</li>
          </ol>
        </div>
      </section>

      <section className="home-section" aria-label="Призив за действие">
        <div className="home-cta">
          <h2 className="home-cta-title">
            Създайте Вашите Дигитални Покани за Сватба Сега
          </h2>
          <p className="home-cta-body">
            Присъединете се към стотици български двойки, които вече използват дигитални покани за сватба и онлайн покани чрез Нашата Сватба. Безплатно, лесно и красиво — започнете сега.
          </p>
          <Link to="/register" className="btn btn-dark btn-xl">
            Започнете Сега — Онлайн Покани
          </Link>
        </div>
      </section>

      <section className="home-faq-section" aria-label="Често задавани въпроси за дигитални покани">
        <header>
          <h2 className="home-section-title">
            Въпроси за Дигитални Покани за Сватба
          </h2>
          <p className="home-section-subtitle">
            Всичко, което трябва да знаете за онлайн поканите за сватба и дигиталните сватбени покани.
          </p>
        </header>
        <div className="home-faqs">
          {INVITATIONS_FAQS.map((faq, i) => (
            <details key={i} className="home-faq-item">
              <summary className="home-faq-question">
                <h3>{faq.q}</h3>
              </summary>
              <p className="home-faq-answer">{faq.a}</p>
            </details>
          ))}
        </div>
      </section>

      <Footer />
    </main>
  );
}

export default OnlineInvitations;
